import { useAtom } from 'jotai'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useEffectEvent } from '../../utilities'
import { deepCompare } from '../../utilities/deep-compare'
import { editorFocusLockAtom, editorFocusTargetAtom, ValidFocusTarget } from '../state'

type UseSimulatorFocusProps = {
	focusTargetId: ValidFocusTarget
	disabled?: boolean
	lockFocus?: boolean
}

type UseSimulatorFocusReturn = {
	isFocused: boolean
	handleFocus: () => void
	mainRef: RefObject<HTMLDivElement | null>
	floatingRef: RefObject<HTMLDivElement | null>
}

export function useSimulatorFocus(props: UseSimulatorFocusProps): UseSimulatorFocusReturn {
	const [focusTarget, setFocusTarget] = useAtom(editorFocusTargetAtom)
	const [focusLock, setFocusLock] = useAtom(editorFocusLockAtom)

	const [isFocused, setIsFocused] = useState(false)

	const mainRef = useRef<HTMLDivElement>(null)
	const floatingRef = useRef<HTMLDivElement>(null)

	const handleDocumentClick = useEffectEvent((event: PointerEvent) => {
		if (focusLock) {
			return
		}

		// if the event has a parent with `data-prevent-focus-loss` return
		if (event.target instanceof HTMLElement && event.target.closest('[data-prevent-focus-loss]')) {
			return
		}

		if (mainRef.current) {
			if (mainRef.current.contains(event.target as Node) || floatingRef.current?.contains(event.target as Node)) {
				return
			}
		}

		setIsFocused(false)
		setFocusTarget({ target: 'none' })
		document.removeEventListener('pointerdown', handleDocumentClick)
	})

	const handleFocus = useCallback(() => {
		// block if already focused
		if (isFocused) {
			return
		}

		// don't allow focus when disabled
		if (props.disabled) {
			return
		}

		// block if focus lock applied and this is a different item
		if (focusLock) {
			return
		}

		setFocusTarget(props.focusTargetId)
	}, [props.focusTargetId, setFocusTarget, isFocused, focusLock])

	useEffect(() => {
		// if this element is disabled, remove focus (unless it's locked)
		if (props.disabled && !focusLock) {
			if (isFocused) {
				setIsFocused(false)
				document.removeEventListener('pointerdown', handleDocumentClick)
			}

			return
		}

		if (deepCompare(focusTarget, props.focusTargetId)) {
			// short circuit if already focused
			if (isFocused || focusLock) {
				return
			}

			setIsFocused(true)

			document.addEventListener('pointerdown', handleDocumentClick)
		} else {
			if (isFocused) {
				setIsFocused(false)
				document.removeEventListener('pointerdown', handleDocumentClick)
			}
		}
	}, [focusTarget, props.focusTargetId, props.disabled, isFocused])

	useEffect(() => {
		setFocusLock(props.lockFocus ?? false)
	}, [props.lockFocus, setFocusLock])

	return {
		isFocused,
		handleFocus,
		mainRef,
		floatingRef,
	}
}
